import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  type: 'job' | 'estimate' | 'call';
  color?: string;
}

interface CalendarProps {
  events?: CalendarEvent[];
}

export default function Calendar({ events = [] }: CalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState<Date[]>([]);

  useEffect(() => {
    generateCalendarDays();
  }, [currentDate]);

  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));

    const days: Date[] = [];
    const current = new Date(startDate);

    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    setCalendarDays(days);
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'job':
        return 'bg-green-500/80 border-green-400';
      case 'estimate':
        return 'bg-orange-500/80 border-orange-400';
      case 'call':
        return 'bg-purple-500/80 border-purple-400';
      default:
        return 'bg-cyan-500/80 border-cyan-400';
    }
  };

  const getEventsForDay = (date: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#30363d]">
        <button
          onClick={previousMonth}
          className="p-1 hover:bg-white/10 rounded transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-gray-400" />
        </button>

        <h3 className="text-sm font-semibold text-white">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h3>

        <button
          onClick={nextMonth}
          className="p-1 hover:bg-white/10 rounded transition-colors"
        >
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Day names */}
      <div className="grid grid-cols-7 gap-px bg-[#30363d] border-b border-[#30363d]">
        {dayNames.map(day => (
          <div
            key={day}
            className="bg-[#1c2128] px-2 py-1 text-center text-xs font-medium text-gray-500"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="flex-1 grid grid-cols-7 gap-px bg-[#30363d] overflow-hidden">
        {calendarDays.map((day, index) => {
          const dayEvents = getEventsForDay(day);
          const today = isToday(day);
          const currentMonth = isCurrentMonth(day);

          return (
            <div
              key={index}
              className={`bg-[#1c2128] p-1 min-h-0 flex flex-col ${
                !currentMonth ? 'opacity-40' : ''
              }`}
            >
              <div
                className={`text-xs font-medium mb-1 ${
                  today
                    ? 'w-5 h-5 rounded-full bg-cyan-500 text-white flex items-center justify-center'
                    : currentMonth
                    ? 'text-gray-300'
                    : 'text-gray-600'
                }`}
              >
                {day.getDate()}
              </div>

              {/* Events */}
              <div className="flex-1 space-y-0.5 overflow-hidden">
                {dayEvents.slice(0, 3).map(event => (
                  <div
                    key={event.id}
                    className={`text-xs px-1 py-0.5 rounded border truncate ${getEventColor(
                      event.type
                    )}`}
                    title={event.title}
                  >
                    <span className="text-white font-medium">{event.title}</span>
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-xs text-gray-500 pl-1">
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 px-3 py-2 border-t border-[#30363d] text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-green-500/80 border border-green-400"></div>
          <span className="text-gray-400">Jobs</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-orange-500/80 border border-orange-400"></div>
          <span className="text-gray-400">Estimates</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-purple-500/80 border border-purple-400"></div>
          <span className="text-gray-400">Calls</span>
        </div>
      </div>
    </div>
  );
}
